import { API_CONFIG } from '@/config/api'

type ResolveImageOptions = {
  siteOrigin?: string | null
  assetBaseUrl?: string | null
  fallback?: string | null
}

type CachedImageOptions = ResolveImageOptions & {
  width?: number
  height?: number
}

const DEFAULT_FALLBACK = '/images/placeholder-course.png'

function stripTrailingSlash(value?: string | null) {
  return String(value || '').replace(/\/+$/, '')
}

function stripLeadingSlash(value?: string | null) {
  return String(value || '').replace(/^\/+/, '')
}

function isAbsoluteUrl(value: string) {
  return /^https?:\/\//i.test(value) || value.startsWith('//')
}

function normalizeAssetBaseUrl(assetBaseUrl?: string | null) {
  const base = stripTrailingSlash(assetBaseUrl)
  if (!base) return ''
  return base.endsWith('/images') ? base : `${base}/images`
}

function buildOpenCartImageUrl(path: string, siteOrigin?: string | null) {
  const origin = stripTrailingSlash(siteOrigin || API_CONFIG.BASE_URL)
  return `${origin}/image/${stripLeadingSlash(path)}`
}

function buildOpenCartCachedImageUrl(
  path: string,
  width: number,
  height: number,
  siteOrigin?: string | null
) {
  const origin = stripTrailingSlash(siteOrigin || API_CONFIG.BASE_URL)
  const cleanPath = stripLeadingSlash(path)

  const lastDot = cleanPath.lastIndexOf('.')
  if (lastDot === -1) {
    return `${origin}/image/${cleanPath}`
  }

  const name = cleanPath.slice(0, lastDot)
  const ext = cleanPath.slice(lastDot + 1)

  return `${origin}/image/cache/${name}-cr-${width}x${height}.${ext}`
}

export function resolveImageUrl(
  input?: string | null,
  opts?: ResolveImageOptions
) {
  const value = String(input || '').trim()
  const siteOrigin = stripTrailingSlash(opts?.siteOrigin || API_CONFIG.BASE_URL)
  const assetBaseUrl = normalizeAssetBaseUrl(opts?.assetBaseUrl)
  const fallback = String(opts?.fallback || DEFAULT_FALLBACK)

  if (!value) return fallback
  if (isAbsoluteUrl(value)) return value

  if (value.includes('/image/cache/') || value.includes('/image/')) {
    if (value.startsWith('/')) {
      return siteOrigin ? `${siteOrigin}${value}` : value
    }
    return value
  }

  if (value.startsWith('wkseller/') || value.startsWith('catalog/')) {
    return buildOpenCartImageUrl(value, siteOrigin)
  }

  if (value.startsWith('users/')) {
    if (assetBaseUrl) {
      return `${assetBaseUrl}/${stripLeadingSlash(value)}`
    }
    return `/images/${stripLeadingSlash(value)}`
  }

  if (value.startsWith('/')) return value

  return `/images/${stripLeadingSlash(value)}`
}

export function getCachedImage(
  path?: string | null,
  width = 100,
  height = 100,
  opts?: ResolveImageOptions
): string {
  const siteOrigin = stripTrailingSlash(opts?.siteOrigin || API_CONFIG.BASE_URL)
  const assetBaseUrl = normalizeAssetBaseUrl(opts?.assetBaseUrl)

  const fallback =
    opts?.fallback ||
    `${siteOrigin}/image/cache/catalog/no_user-cr-${width}x${height}.png`

  const value = String(path || '').trim()
  if (!value) return fallback

  if (isAbsoluteUrl(value) || value.includes('/image/cache/')) {
    return value
  }

  if (value.startsWith('/image/')) {
    return `${siteOrigin}${value}`
  }

  if (value.startsWith('users/')) {
    if (assetBaseUrl) {
      return `${assetBaseUrl}/${stripLeadingSlash(value)}`
    }
    return `/images/${stripLeadingSlash(value)}`
  }

  if (value.startsWith('wkseller/') || value.startsWith('catalog/')) {
    return buildOpenCartCachedImageUrl(value, width, height, siteOrigin)
  }

  if (/-cr-\d+x\d+\.[^/]+$/i.test(value)) {
    return buildOpenCartImageUrl(value, siteOrigin)
  }

  if (value.startsWith('/')) {
    return `${siteOrigin}${value}`
  }

  return resolveImageUrl(value, {
    siteOrigin,
    assetBaseUrl,
    fallback,
  })
}

export function getImageCandidates(
  path?: string | null,
  opts?: CachedImageOptions
): string[] {
  const siteOrigin = stripTrailingSlash(opts?.siteOrigin || API_CONFIG.BASE_URL)
  const assetBaseUrl = normalizeAssetBaseUrl(opts?.assetBaseUrl)
  const width = Number(opts?.width || 100)
  const height = Number(opts?.height || 100)

  const fallback =
    opts?.fallback ||
    `${siteOrigin}/image/cache/catalog/no_user-cr-${width}x${height}.png`

  const value = String(path || '').trim()
  if (!value) return [fallback]

  if (isAbsoluteUrl(value)) return [value, fallback]

  if (value.startsWith('users/')) {
    const candidates: string[] = []

    if (assetBaseUrl) {
      candidates.push(`${assetBaseUrl}/${stripLeadingSlash(value)}`)
    }

    candidates.push(`/images/${stripLeadingSlash(value)}`)
    candidates.push(fallback)

    return Array.from(new Set(candidates))
  }

  if (value.startsWith('wkseller/') || value.startsWith('catalog/')) {
    return Array.from(
      new Set([
        buildOpenCartCachedImageUrl(value, width, height, siteOrigin),
        buildOpenCartImageUrl(value, siteOrigin),
        fallback,
      ])
    )
  }

  if (value.startsWith('/')) {
    return Array.from(new Set([value, fallback]))
  }

  return Array.from(
    new Set([
      resolveImageUrl(value, { siteOrigin, assetBaseUrl, fallback }),
      fallback,
    ])
  )
}